/**
 * CodeEditor - CodeMirror 6 wrapper component for CodeNode
 *
 * This component provides a professional code editing experience with:
 * - Syntax highlighting for Python and JavaScript
 * - Line numbers and code folding
 * - Autocomplete suggestions
 * - Cmd/Ctrl+Enter shortcut to execute code
 * - Dark theme matching the application UI
 *
 * Uses @uiw/react-codemirror which wraps CodeMirror 6 for React.
 * Much smaller bundle size than Monaco (~300KB vs ~5-10MB).
 */

import { useMemo, useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { keymap } from '@codemirror/view';
import type { CodeLanguage } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

export interface CodeEditorProps {
  /**
   * Current code content in the editor
   */
  code: string;

  /**
   * Programming language for syntax highlighting
   */
  language: CodeLanguage;

  /**
   * Callback when code content changes
   * @param value - New code content
   */
  onChange: (value: string) => void;

  /**
   * Callback when user triggers execution (Cmd/Ctrl+Enter)
   */
  onExecute: () => void;

  /**
   * Whether the editor should be read-only
   * @default false
   */
  readOnly?: boolean;

  /**
   * Placeholder text when editor is empty
   */
  placeholder?: string;

  /**
   * Minimum height of the editor
   * @default '200px'
   */
  minHeight?: string;

  /**
   * Maximum height of the editor (for scrolling)
   * @default '400px'
   */
  maxHeight?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * CodeEditor Component
 *
 * A wrapper around CodeMirror 6 that provides a consistent editing experience
 * for both Python and JavaScript code.
 *
 * Key features:
 * - Language-aware syntax highlighting
 * - Execute shortcut (Cmd/Ctrl+Enter)
 * - Dark theme with good contrast
 * - Auto-complete and bracket matching
 *
 * The component uses useMemo to prevent recreating extensions on every render,
 * which would cause performance issues with CodeMirror.
 */
export function CodeEditor({
  code,
  language,
  onChange,
  onExecute,
  readOnly = false,
  placeholder = 'Enter your code here...',
  minHeight = '200px',
  maxHeight = '400px',
}: CodeEditorProps) {
  // ==================== EXTENSIONS ====================

  /**
   * Memoize language extension to prevent recreation on every render.
   * CodeMirror recreates the editor state when extensions change,
   * so we only want to change when the language actually changes.
   */
  const languageExtension = useMemo(() => {
    switch (language) {
      case 'python':
        return python();
      case 'javascript':
        return javascript({ jsx: true, typescript: true });
      default:
        return python(); // Default to Python
    }
  }, [language]);

  /**
   * Create a custom keymap for the execute shortcut.
   * Cmd/Ctrl+Enter triggers code execution.
   */
  const executeKeymap = useMemo(() => {
    return keymap.of([
      {
        // Cmd+Enter on Mac, Ctrl+Enter on Windows/Linux
        key: 'Mod-Enter',
        run: () => {
          onExecute();
          return true; // Prevent default behavior
        },
      },
    ]);
  }, [onExecute]);

  /**
   * Combine all extensions into a single array.
   * Order matters - language should come before theme.
   */
  const extensions = useMemo(() => {
    return [languageExtension, executeKeymap];
  }, [languageExtension, executeKeymap]);

  // ==================== HANDLERS ====================

  /**
   * Handle code changes from CodeMirror.
   * The onChange callback provides the new value directly.
   */
  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange]
  );

  // ==================== RENDER ====================

  return (
    <div
      // nodrag/nopan classes prevent React Flow from capturing drag events
      // This allows normal text selection and cursor movement in the editor
      className="nodrag nopan rounded-lg overflow-hidden border border-gray-200"
      style={{
        minHeight,
        maxHeight,
      }}
    >
      <CodeMirror
        value={code}
        extensions={extensions}
        theme={oneDark}
        onChange={handleChange}
        readOnly={readOnly}
        placeholder={placeholder}
        basicSetup={{
          // Line numbers in the gutter
          lineNumbers: true,
          // Highlight the current line
          highlightActiveLine: true,
          // Highlight matching text when selecting
          highlightSelectionMatches: true,
          // Enable autocompletion
          autocompletion: true,
          // Enable code folding
          foldGutter: true,
          // Enable bracket matching
          bracketMatching: true,
          // Close brackets automatically
          closeBrackets: true,
          // Indent on tab
          indentOnInput: true,
          // Draw selection for better visibility
          drawSelection: true,
          // Enable history (undo/redo)
          history: true,
        }}
        style={{
          // Font settings for code
          fontSize: '14px',
          fontFamily: 'JetBrains Mono, SF Mono, Monaco, Consolas, monospace',
          // Size constraints
          minHeight,
          maxHeight,
          overflow: 'auto',
        }}
      />
    </div>
  );
}

// Export as default as well for convenience
export default CodeEditor;
